import client from 'api/client'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useAuth } from 'hooks/UseAuth'
import InfoModal from 'components/InfoModal/InfoModal'
import { SettingsGuest, SettingsMember } from 'types'
import { getRemoveUserErrorMessage } from '../utils'
import { reportError } from 'utils/error'
import Styles from './ManageMembers.module.scss'
import { useState } from 'react'

interface Props {
  visible: boolean
  setVisible: (x: boolean) => void
  onSuccess: () => void
  /** Receives the member the request was for so a slow removal can't clear a newer target */
  onDone: (memberId?: number) => void
  memberToRemove: SettingsMember | SettingsGuest | null
}

export const RemoveUserConfirmationModal = ({ visible, setVisible, onSuccess, onDone, memberToRemove }: Props) => {
  const auth = useAuth()
  const [submitting, setSubmitting] = useState<boolean>(false)

  const selectedUserName = () => (memberToRemove ? `${memberToRemove?.first_name} ${memberToRemove?.last_name}` : '')

  const removeUser = async (id: number, type: SettingsMember['type'] | SettingsGuest['type']) => {
    if (type === 'DeletedMembership') {
      showErrorMessageToast('User has already been removed')
      return
    }
    const res: any = await client.deleteMember(id, auth ? auth.company_key : '')
    if (res.type === 'success') {
      showToastMessage('User was removed', 'success')
      onSuccess()
    } else {
      const errorMessage = getRemoveUserErrorMessage(res.status)
      showErrorMessageToast(errorMessage)
      if (res.type === 'error')
        reportError(new Error('ManageMembers received an error response'), {
          severity: 'warning',
          metaData: { info: 'Remove member' },
        })
    }
    onDone(id)
    setSubmitting(false)
  }

  return (
    <InfoModal
      onOk={() => {
        if (!memberToRemove) return false
        setSubmitting(true)
        removeUser(memberToRemove.id, memberToRemove.type)
        return true
      }}
      onCancel={onDone}
      okText="Yes, remove user"
      cancelText="No, cancel"
      title="Please confirm..."
      visible={visible}
      setVisible={setVisible}
      actionsDisabled={submitting}
    >
      <p data-testid="remove-user-confirmation-modal-content">Are you sure you want to remove <b>{selectedUserName()}</b> from this workspace?</p>
      <div className={Styles.warning}>
        This action will remove any access to documents, folders, handbooks and groups for this user.
      </div>
    </InfoModal>
  )
}
